/**
 * MessageBubble - Individual message display
 */

import React from 'react';
import { Message } from '../types/chat';
import { formatTimestamp } from '../utils/formatTimestamp';

interface MessageBubbleProps {
  message: Message;
  showAvatar: boolean; // Show avatar (first in group)
  showName: boolean; // Show sender name (first in group for received messages)
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  showAvatar,
  showName,
}) => {
  if (message.isMine) {
    // Sent messages (right-aligned, blue)
    return (
      <div className="flex justify-end mb-2">
        <div className="max-w-[70%]">
          <div className="bg-blue-500 text-white rounded-2xl rounded-tr-sm px-4 py-2">
            <p className="text-sm whitespace-pre-wrap break-words">{message.body}</p>
          </div>
          <div className="text-xs text-gray-400 mt-1 text-right">
            {formatTimestamp(message.createdAt)}
          </div>
        </div>
      </div>
    );
  }

  // Received messages (left-aligned, gray)
  return (
    <div className="flex items-start mb-2 space-x-2">
      {/* Avatar (or placeholder) */}
      <div className="flex-shrink-0" style={{ width: '32px' }}>
        {showAvatar ? (
          message.senderAvatar ? (
            <img
              src={message.senderAvatar}
              alt={message.senderName}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-xs text-gray-600">
                {message.senderName.charAt(0).toUpperCase()}
              </span>
            </div>
          )
        ) : (
          <div className="w-8 h-8" /> // Empty space to maintain alignment
        )}
      </div>

      {/* Message content */}
      <div className="max-w-[70%]">
        {showName && (
          <div className="text-xs text-gray-500 mb-1 ml-1">{message.senderName}</div>
        )}
        <div className="bg-gray-200 text-gray-800 rounded-2xl rounded-tl-sm px-4 py-2">
          <p className="text-sm whitespace-pre-wrap break-words">{message.body}</p>
        </div>
        <div className="text-xs text-gray-400 mt-1 ml-1">
          {formatTimestamp(message.createdAt)}
        </div>
      </div>
    </div>
  );
};

